<script setup>
import ChipEventType from '@/components/ChipEventType.vue'
import ChipPoints from '@/components/ChipPoints.vue'
import DateRange from '@/components/DateRange.vue'
import { getMonthName } from '@/lib/dates'
import { eventStateColor } from '@/lib/events'

/**
 * Reusable grid of event cards.
 *
 * It only renders the list it is given: loading, filtering and error states stay with the
 * caller, so the same component works on the events page and on the play page.
 */
defineProps({
  /** Events, in the shape returned by GET /api/events. */
  events: { type: Array, default: () => [] },
  /** Grid attributes for each card column — override them when the container is narrower. */
  cols: { type: [Number, String], default: 12 },
  md: { type: [Number, String], default: 6 },
  lg: { type: [Number, String], default: 4 },
})
</script>

<template>
  <v-row>
    <v-col v-for="event in events" :key="event.id" :cols="cols" :md="md" :lg="lg">
      <v-card
        :to="{ name: 'event_detail', params: { id: event.id } }"
        hover
        elevation="3"
        rounded="lg"
        class="h-100 d-flex flex-column"
      >
        <v-card-item class="pa-4">
          <div class="d-flex flex-wrap align-center ga-2">
            <ChipEventType :type="event.eventType" />
            <v-chip :color="eventStateColor(event.state)" size="small" variant="tonal" label>
              {{ $t(`eventStates.${event.state}`) }}
            </v-chip>
          </div>
          <v-card-subtitle class="mt-2">
            <v-icon size="14" class="mr-1">mdi-calendar-range</v-icon>
            <DateRange :from="event.dateStart" :to="event.dateEnd" />
          </v-card-subtitle>
        </v-card-item>

        <v-card-text class="pt-0 flex-grow-1">
          <p v-if="event.eventType == 'monthly_season'" class="text-body-2 text-medium-emphasis">
            {{ $t(`eventNames.${event.eventType}`, { month: getMonthName(event.dateStart) }) }}
          </p>

          <!-- An event covering every track of the game gets one chip of its own instead of the
               whole list (the flag comes from the API, see EventLib::coversEveryTrack): a
               different colour and icon, so it can't be mistaken for a specific track. -->
          <div class="d-flex flex-wrap align-center ga-2 mt-3">
            <v-chip
              v-if="event.allTracks"
              color="brown"
              size="small"
              variant="tonal"
              prepend-icon="mdi-earth"
            >
              {{ $t('events.allTracks') }}
            </v-chip>

            <template v-else>
              <v-chip
                v-for="track in event.tracks"
                :key="track.id"
                size="small"
                variant="tonal"
                prepend-icon="mdi-flag-checkered"
                :title="track.country ? $t(`countries.${track.country}`) : ''"
              >
                {{ $t(`tracks.${track.trackKey}`) }}
              </v-chip>
            </template>
          </div>

          <div v-if="event.usesLaps" class="mt-3 text-caption text-medium-emphasis">
            <v-icon size="14" class="mr-1">mdi-counter</v-icon>
            {{ $t('events.laps') }}: {{ event.defaultLaps }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <template v-if="event.myEntry">
            <v-icon color="primary" class="mr-2">mdi-account-check</v-icon>
            <span class="text-body-2 font-weight-medium">{{ $t('events.yourResult') }}</span>
            <v-spacer />
            <v-chip color="primary" size="small" variant="flat" label>
              {{ $t('events.position') }} {{ event.myEntry.position }}
            </v-chip>
            <ChipPoints v-if="event.myEntry.points !== null" :points="event.myEntry.points" />
            <v-chip v-else-if="event.myEntry.resultTime" size="small" variant="tonal" label>
              {{ event.myEntry.resultTime }}
            </v-chip>
          </template>

          <template v-else>
            <v-icon class="mr-2">mdi-clock-outline</v-icon>
            <span class="text-body-2 text-medium-emphasis">{{ $t('events.todo') }}</span>
            <v-spacer />
            <v-icon size="18">mdi-chevron-right</v-icon>
          </template>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>
